import { StyledReactSelect } from '@fountain/fountain-ui-components';
import { Grid } from '@material-ui/core';
import { produce } from 'immer';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import {
  CONSENT_CALLS_MARKETING,
  CONSENT_CALLS_TRANSACTIONAL,
  CONSENT_SMS_MARKETING,
  CONSENT_SMS_TRANSACTIONAL,
  getConditionErrorMessage,
} from 'containers/WorkflowEditor/components/Rules/Condition/constants';
import { messages } from 'containers/WorkflowEditor/components/Rules/Condition/messages';
import globalMessages from 'shared/global/messages';

import { DocumentSigningRuleProps } from '../../DocumentSigningRules/types';
import { RulesProps, SharedConditionProps } from '../../types';

export const CommunicationConsent: FC<SharedConditionProps> = ({
  condition,
  ruleId,
  setRules,
  setDocumentSigningRules,
  errors,
}) => {
  const intl = useIntl();

  const { extra } = condition;

  const consentTypeOptions = [
    {
      label: intl.formatMessage(messages.consentSmsMarketing),
      value: CONSENT_SMS_MARKETING,
    },
    {
      label: intl.formatMessage(messages.consentSmsTransactional),
      value: CONSENT_SMS_TRANSACTIONAL,
    },
    {
      label: intl.formatMessage(messages.consentCallsMarketing),
      value: CONSENT_CALLS_MARKETING,
    },
    {
      label: intl.formatMessage(messages.consentCallsTransactional),
      value: CONSENT_CALLS_TRANSACTIONAL,
    },
  ];

  const consentValueOptions = [
    { label: intl.formatMessage(globalMessages.yes), value: true },
    { label: intl.formatMessage(globalMessages.no), value: false },
  ];

  const selectedType =
    consentTypeOptions.find(option => option.value === extra.key) ?? null;
  const selectedValue =
    consentValueOptions.find(option => option.value === extra.consent) ?? null;

  const onChangeType = (newType: { label: string; value: string }) => {
    const rulesSetter = setRules ?? setDocumentSigningRules;

    if (rulesSetter) {
      rulesSetter(
        produce((draftRules: RulesProps | DocumentSigningRuleProps) => {
          const draftConditions =
            draftRules[ruleId]?.condition_set_attributes
              ?.conditions_attributes ?? [];
          const idx = draftConditions.findIndex(
            cond => cond.id === condition.id,
          );

          draftConditions[idx].extra.key = newType.value;
        }),
      );
    }
  };

  const onChangeValue = (newValue: { label: string; value: boolean }) => {
    const rulesSetter = setRules ?? setDocumentSigningRules;

    if (rulesSetter) {
      rulesSetter(
        produce((draftRules: RulesProps | DocumentSigningRuleProps) => {
          const draftConditions =
            draftRules[ruleId]?.condition_set_attributes
              ?.conditions_attributes ?? [];
          const idx = draftConditions.findIndex(
            cond => cond.id === condition.id,
          );

          draftConditions[idx].extra.consent = newValue.value;
        }),
      );
    }
  };

  const keyErrorMessage =
    extra?.key === undefined && getConditionErrorMessage('key', errors);
  const valueErrorMessage =
    extra?.consent === undefined && getConditionErrorMessage('consent', errors);

  return (
    <>
      <Grid item xs={6}>
        <StyledReactSelect
          options={consentTypeOptions}
          label={intl.formatMessage(messages.consentType)}
          aria-label={intl.formatMessage(messages.consentType)}
          onChange={onChangeType}
          value={selectedType}
          error={keyErrorMessage}
          placeholder=""
          required
        />
      </Grid>
      <Grid item xs={6}>
        <StyledReactSelect
          options={consentValueOptions}
          label={intl.formatMessage(globalMessages.status)}
          aria-label={intl.formatMessage(globalMessages.status)}
          onChange={onChangeValue}
          value={selectedValue}
          error={valueErrorMessage}
          placeholder=""
          required
        />
      </Grid>
    </>
  );
};
